import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";

import { ContentProvider, useContent } from "@/context/ContentContext";
import { AuthProvider } from "@/context/AuthContext";

import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import WhatIf from "@/components/WhatIf";
import About from "@/components/About";
import ConsultBand from "@/components/ConsultBand";
import TreatmentAreas from "@/components/TreatmentAreas";
import Booking from "@/components/Booking";
import Contact from "@/components/Contact";
import Footer from "@/components/Footer";
import { scrollToId } from "@/lib/scroll";

import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import TreatmentAreaPage from "@/pages/TreatmentAreaPage";
import Login from "@/admin/Login";
import ForgotPassword from "@/admin/ForgotPassword";
import UpdatePassword from "@/admin/UpdatePassword";
import ProtectedRoute from "@/admin/ProtectedRoute";
import AdminLayout from "@/admin/AdminLayout";
import ContentEditor from "@/admin/ContentEditor";
import ThemeEditor from "@/admin/ThemeEditor";
import BlogManager from "@/admin/BlogManager";
import BlogEditor from "@/admin/BlogEditor";
import SeoEditor from "@/admin/SeoEditor";
import AdminsManager from "@/admin/AdminsManager";
import SEO from "@/seo/SEO";
import { STATIC_PUBLIC_ROUTES, buildLocalBusinessJsonLd, getPageSeo, composeTitle, getDefaultDescription } from "@/seo/routeConfig";

const homeSeoDefaults = STATIC_PUBLIC_ROUTES.find((r) => r.path === "/").seo;

const Home = () => {
  const { content } = useContent();
  const override = getPageSeo(content, "home");
  const title = composeTitle(override.title || homeSeoDefaults.title, content);
  const description = override.description || homeSeoDefaults.description || getDefaultDescription(content);
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), smoothWheel: true });
    let rafId;
    window.__lenis = lenis;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);

    const pending = sessionStorage.getItem("kb_scroll");
    if (pending) {
      sessionStorage.removeItem("kb_scroll");
      setTimeout(() => scrollToId(pending), 400);
    }

    return () => {
      cancelAnimationFrame(rafId);
      window.__lenis = null;
      lenis.destroy();
    };
  }, []);

  return (
    <div className="relative">
      <SEO
        path="/"
        title={title}
        description={description}
        jsonLd={buildLocalBusinessJsonLd(content)}
      />
      <Navbar />
      <main>
        <Hero />
        <WhatIf />
        <About />
        <ConsultBand />
        <TreatmentAreas />
        <Booking />
        <Contact />
      </main>
      <Footer />
    </div>
  );
};

const AdminShell = ({ children }) => (
  <ProtectedRoute>
    <AdminLayout>{children}</AdminLayout>
  </ProtectedRoute>
);

// Reset scroll to the top on every route change (skips in-page anchor jumps).
// Without this, React Router keeps the previous scroll position, which meant
// clicking a treatment tile from the homepage grid dropped visitors near the
// footer of the new page.
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) return; // let anchor links (#treatment etc.) resolve themselves
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname, hash]);
  return null;
};

function App() {
  return (
    <div className="App">
      <Toaster position="top-center" richColors />
      <AuthProvider>
        <ContentProvider>
          <BrowserRouter>
            <ScrollToTop />
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/treatment-areas/:slug" element={<TreatmentAreaPage />} />

              <Route path="/admin/login" element={<Login />} />
              <Route path="/admin/forgot-password" element={<ForgotPassword />} />
              <Route path="/admin/update-password" element={<UpdatePassword />} />
              <Route path="/admin" element={<AdminShell><ContentEditor /></AdminShell>} />
              <Route path="/admin/theme" element={<AdminShell><ThemeEditor /></AdminShell>} />
              <Route path="/admin/blog" element={<AdminShell><BlogManager /></AdminShell>} />
              <Route path="/admin/blog/new" element={<AdminShell><BlogEditor /></AdminShell>} />
              <Route path="/admin/blog/:id" element={<AdminShell><BlogEditor /></AdminShell>} />
              <Route path="/admin/seo" element={<AdminShell><SeoEditor /></AdminShell>} />
              <Route path="/admin/admins" element={<AdminShell><AdminsManager /></AdminShell>} />
            </Routes>
          </BrowserRouter>
        </ContentProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
